// [목적] 폴리오 PDF 내보내기 상태 머신 훅 — 요청 → 2초 간격 폴링(최대 3분) → done/failed
// [이유] FlowLine A2(49df7920): FolioExportButton 이 이 훅 하나로 요청/폴링/타임아웃/재시도를 처리.
// [사이드 이팩트] setInterval 기반 폴링 — 언마운트/재시작 시 clearInterval 정리.
// [수정 시 고려사항] 3분 초과 시 timedOut=true 로 전환하고 폴링 중단 — reset() 으로 재시도 가능.

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  getFolioExportStatus,
  requestFolioExport,
  type FolioExportStatusResponse,
} from '../api/folio-export.client.js';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_DURATION_MS = 3 * 60 * 1000;

export interface UseFolioExportResult {
  /** 내보내기 시작 — idle/failed/timedOut 상태에서만 의미 있음 */
  start: () => void;
  isStarting: boolean;
  exportId: string | null;
  status: FolioExportStatusResponse | null;
  /** 3분 내 done/failed 에 도달하지 못함 */
  timedOut: boolean;
  /** 요청 자체(POST) 실패 */
  startError: boolean;
  /** idle 로 되돌려 재시도 가능하게 함 */
  reset: () => void;
}

export function useFolioExport(folioId: string): UseFolioExportResult {
  const [exportId, setExportId] = useState<string | null>(null);
  const [status, setStatus] = useState<FolioExportStatusResponse | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [startError, setStartError] = useState(false);
  const startedAtRef = useRef<number | null>(null);

  const reset = useCallback((): void => {
    setExportId(null);
    setStatus(null);
    setTimedOut(false);
    setStartError(false);
    startedAtRef.current = null;
  }, []);

  const start = useCallback((): void => {
    setIsStarting(true);
    setStartError(false);
    setTimedOut(false);
    requestFolioExport(folioId)
      .then((res) => {
        startedAtRef.current = Date.now();
        setExportId(res.exportId);
        setStatus({ status: res.status, pdfUrl: null, expiresAt: null, errorCode: null });
      })
      .catch(() => setStartError(true))
      .finally(() => setIsStarting(false));
  }, [folioId]);

  // [목적] exportId 발급 후 2초 간격 폴링 — done/failed 도달 또는 3분 경과 시 중단
  // [이유] A2: "GET .../export/:exportId 폴링(2s 간격·최대 3분)"
  // [사이드 이팩트] setInterval 등록 — cleanup 에서 반드시 clearInterval
  // [수정 시 고려사항] exportId 가 바뀌면(재시도) 이전 interval 은 cleanup 으로 정리되고 새로 시작.
  useEffect(() => {
    if (!exportId) return;
    if (status?.status === 'done' || status?.status === 'failed') return;

    const interval = setInterval(() => {
      const startedAt = startedAtRef.current;
      if (startedAt && Date.now() - startedAt > MAX_POLL_DURATION_MS) {
        setTimedOut(true);
        clearInterval(interval);
        return;
      }
      void getFolioExportStatus(folioId, exportId)
        .then((res) => setStatus(res))
        .catch(() => {
          // 일시적 네트워크 오류는 다음 tick 에 재시도(폴링 자체를 중단하지 않음)
        });
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [folioId, exportId, status?.status]);

  return { start, isStarting, exportId, status, timedOut, startError, reset };
}
